import { AfterViewInit, Component, ElementRef, EventEmitter, HostBinding, Input, NgZone, OnChanges, OnDestroy, Output, SimpleChanges, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatTableDataSource } from '@angular/material/table';
import { DialogComponent } from 'src/app/components/dialog/dialog.component';
import { DialogData } from 'src/app/models/dialog-data';

export type ListaDadosTipoEditor = 'texto' | 'numero' | 'data' | 'combobox' | 'checkbox' | 'cor';

export interface ListaDadosValidacao {
  obrigatorio?: boolean;
  min?: number;
  max?: number;
  minLength?: number;
  maxLength?: number;
  regex?: string;
  mask?: string;
}

export interface ListaDadosConfigEdicao {
  tipoEditor: ListaDadosTipoEditor;
  opcoes?: any[];
  campoChave?: string;
  campoRotulo?: string;
  filtroCombo?: (opcao: any, row: any) => boolean;
  validacao?: ListaDadosValidacao;
}

export interface ListaDadosColuna {
  campo: string;
  campoExibicao?: string;
  rotulo: string;
  tamanho: string;
  formatador?: (row: any, valor: any) => string;
  tooltip?: (row: any) => string;
  classeValor?: (row: any, valor: any) => string | string[] | Record<string, boolean>;
  corValor?: (row: any, valor: any) => string | undefined;
  corTextoValor?: (row: any, valor: any) => string | undefined;
  icone?: (row: any, valor: any) => string | undefined;
  iconeSecundario?: (row: any, valor: any) => string | undefined;
  classeIcone?: (row: any, valor: any) => string | string[] | Record<string, boolean>;
  editavel?: boolean;
  tipoExibicao?: 'texto' | 'status' | 'cor' | 'etiqueta';
  edicao?: ListaDadosConfigEdicao;
}

export interface ListaDadosAcaoLinha {
  chave: string;
  texto: string;
  icone: string;
  visivel?: (row: any) => boolean;
  desabilitada?: (row: any) => boolean;
}

@Component({
  selector: 'app-lista-dados',
  templateUrl: './lista-dados.component.html',
  styleUrls: ['./lista-dados.component.css']
})
export class ListaDadosComponent implements OnChanges, AfterViewInit, OnDestroy {
  private readonly marcadorLinhaNova = '__listaDadosLinhaNova';

  @ViewChild(MatPaginator) private paginator?: MatPaginator;
  @ViewChild('inputFiltroRapido') private inputFiltroRapido?: any;
  @ViewChild('rolagemTabela', { read: ElementRef }) private rolagemTabela?: ElementRef<HTMLElement>;
  @ViewChild('tabela', { read: ElementRef }) private tabela?: ElementRef<HTMLElement>;
  public podeRolarParaEsquerda = false;
  public podeRolarParaDireita = false;
  private observadorTamanho?: ResizeObserver;
  private rolagemTimer?: number;

  @Input() public upTitulo = 'Lista de Dados';
  @Input() public upIcone = 'list_alt';
  @Input() public upCor = '3f51b5';
  @Input() public upLargura = 3;

  @Input() public upDados: any[] = [];
  @Input() public upColunas: ListaDadosColuna[] = [];

  @Input() public upHabilitaBotaoIncluir = true;
  @Input() public upHabilitaBotaoEditar = true;
  @Input() public upHabilitaBotaoExcluir = true;
  @Input() public upHabilitaContador = false;

  @Input() public upHabilitaFiltroRapido = false;
  @Input() public upFiltroRapido?: (row: any, filtro: string) => boolean;
  @Input() public upHabilitaPaginacao = false;
  @Input() public upCarregando = false;
  @Input() public upHabilitaEditarLinha = false;
  @Input() public upPermitirEdicaoLinhaExistente = true;
  @Input() public upTextoBotaoEditar = 'Editar';
  @Input() public upIconeBotaoEditar = 'edit';
  @Input() public upTextoBotaoExcluir = 'Excluir';
  @Input() public upIconeBotaoExcluir = 'delete';
  @Input() public upAcoesLinha: ListaDadosAcaoLinha[] = [];
  @Input() public upVisivelBotaoEditar: (row: any) => boolean = () => true;
  @Input() public upVisivelBotaoExcluir: (row: any) => boolean = () => true;
  @Input() public upClasseLinha: (row: any) => string | string[] | Record<string, boolean> = () => '';

  public filtroRapidoVisivel = false;

  @Input() public upTotalRegistros = 0;
  @Input() public upPaginaAtual = 0;
  @Input() public upItensPorPagina = 10;
  @Input() public upOpcoesItensPorPagina: number[] = [10, 25, 50, 100];

  @Output() public ueBotaoIncluir = new EventEmitter<void>();
  @Output() public ueBotaoEditar = new EventEmitter<any>();
  @Output() public ueBotaoExcluir = new EventEmitter<any>();
  @Output() public ueAcaoLinha = new EventEmitter<{ chave: string; linha: any }>();
  @Output() public ueMudouPagina = new EventEmitter<PageEvent>();
  @Output() public ueIniciarEdicaoLinha = new EventEmitter<any>();
  @Output() public ueSalvarLinha = new EventEmitter<any>();
  @Output() public ueCancelarEdicaoLinha = new EventEmitter<any>();
  @Output() public ueMudouValorLinha = new EventEmitter<{ linha: any; campo: string; valor: any }>();

  public dataSource = new MatTableDataSource<any>([]);
  public filtroRapido = '';
  public linhaSelecionada: any;
  public linhaEmEdicao: any | null = null;
  private linhaOriginal: Record<string, any> | null = null;
  private linhaCriadaEmEdicao = false;

  public constructor(
    private dialog: MatDialog,
    private zone: NgZone,
  ) { }

  public ngAfterViewInit(): void {
    this.configurarPaginador();
    this.observadorTamanho = new ResizeObserver(() => {
      this.zone.run(() => this.atualizarSetasRolagem());
    });
    if (this.rolagemTabela && this.tabela) {
      this.observadorTamanho.observe(this.rolagemTabela.nativeElement);
      this.observadorTamanho.observe(this.tabela.nativeElement);
    }
  }

  public ngOnDestroy(): void {
    this.pararRolagem();
    this.observadorTamanho?.disconnect();
  }

  public atualizarSetasRolagem(): void {
    const container = this.rolagemTabela?.nativeElement;
    if (!container) {
      return;
    }
    this.podeRolarParaEsquerda = container.scrollLeft > 1;
    this.podeRolarParaDireita = container.scrollLeft + container.clientWidth < container.scrollWidth - 1;
  }

  public iniciarRolagem(direcao: 1 | -1): void {
    this.pararRolagem();
    let velocidade = 0.5;
    this.rolagemTimer = window.setInterval(() => {
      const container = this.rolagemTabela?.nativeElement;
      if (!container) {
        this.pararRolagem();
        return;
      }
      const limite = container.scrollWidth - container.clientWidth;
      container.scrollLeft = Math.max(0, Math.min(limite, container.scrollLeft + direcao * velocidade));
      this.atualizarSetasRolagem();
      if (direcao === -1 ? !this.podeRolarParaEsquerda : !this.podeRolarParaDireita) {
        this.pararRolagem();
      }
      velocidade = Math.min(velocidade + 0.35, 10);
    }, 16);
  }

  public pararRolagem(): void {
    if (this.rolagemTimer !== undefined) {
      window.clearInterval(this.rolagemTimer);
      this.rolagemTimer = undefined;
    }
  }

  public rolarTabela(direcao: 1 | -1): void {
    const container = this.rolagemTabela?.nativeElement;
    container?.scrollBy({ left: direcao * container.clientWidth / 2, behavior: 'smooth' });
  }

  @HostBinding('style.display')
  public get hostDisplay(): string {
    return this.larguraNormalizada === 0 ? 'none' : 'block';
  }

  @HostBinding('style.width')
  public get hostWidth(): string {
    return this.larguraCss;
  }

  @HostBinding('style.max-width')
  public get hostMaxWidth(): string {
    return this.larguraCss;
  }

  @HostBinding('style.--acoes-coluna-largura')
  public get acoesColunaLarguraCss(): string {
    switch (this.larguraNormalizada) {
      case 1:
        return '16%';
      case 2:
        return '12%';
      default:
        return '8%';
    }
  }

  @HostBinding('style.flex')
  public get hostFlex(): string {
    return this.larguraNormalizada === 0 ? '0 0 auto' : `0 0 ${this.larguraCss}`;
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes['upHabilitaPaginacao']) {
      this.configurarPaginador();
    }

    if (changes['upDados']) {
      const estavaEditando = this.editandoLinhaAtiva;

      const prev = changes['upDados'].previousValue;
      const prevCount = Array.isArray(prev) ? prev.length : 0;
      const newCount = Array.isArray(changes['upDados'].currentValue) ? changes['upDados'].currentValue.length : 0;
      this.dataSource.data = this.upDados ?? [];

      if (prevCount > 0 && newCount === 0 && this.filtroRapido) {
        this.limparFiltroRapido();
      } else {
        this.aplicarFiltroRapido(this.filtroRapido);
      }

      this.limparEdicao();

      if (estavaEditando) {
        this.focarBotaoAdicionar();
      }
    }
  }

  public get colunasExibidas(): string[] {
    const colunas = this.upColunas.map(c => c.campo);
    return this.exibeColunaAcoes ? [...colunas, 'acoes'] : colunas;
  }

  public get exibeColunaAcoes(): boolean {
    return this.upHabilitaBotaoIncluir || this.upHabilitaBotaoEditar || this.upHabilitaBotaoExcluir || this.upAcoesLinha.length > 0;
  }

  public get editandoLinhaAtiva(): boolean {
    return !!this.linhaEmEdicao;
  }

  public get totalRegistrosPaginacao(): number {
    return this.upTotalRegistros > 0 ? this.upTotalRegistros : this.upDados.length;
  }

  public get semRegistros(): boolean {
    return (this.upDados?.length ?? 0) === 0;
  }

  public get larguraNormalizada(): 0 | 1 | 2 | 3 {
    const largura = Number(this.upLargura);

    if (largura === 0 || largura === 1 || largura === 2 || largura === 3) {
      return largura;
    }

    return 3;
  }

  public get larguraCss(): string {
    switch (this.larguraNormalizada) {
      case 0:
        return '0';
      case 1:
        return '33.3333%';
      case 2:
        return '66.6667%';
      default:
        return '100%';
    }
  }

  public get corHex(): string {
    const hex = (this.upCor ?? '').replace('#', '').trim();
    return hex ? `#${hex}` : '#3f51b5';
  }

  public get colunasEditaveis(): ListaDadosColuna[] {
    return this.upColunas.filter(c => this.podeEditarColuna(c));
  }

  public aplicarFiltroRapido(valor: string): void {
    this.filtroRapido = valor;

    this.dataSource.filterPredicate = (data: any, filter: string) => {
      if (this.isLinhaEmEdicao(data) || this.isLinhaNovaSemId(data)) {
        return true;
      }

      const filtro = (filter ?? '').toLowerCase();
      if (!filtro) {
        return true;
      }

      if (this.upFiltroRapido) {
        return this.upFiltroRapido(data, filtro);
      }

      return this.upColunas.some(coluna => {
        const valorCampo = this.obterValorCampo(data, coluna.campo);
        return `${valorCampo ?? ''}`.toLowerCase().includes(filtro);
      });
    };

    this.dataSource.filter = valor.trim().toLowerCase();
  }

  public alternarFiltroRapido(): void {
    this.filtroRapidoVisivel = !this.filtroRapidoVisivel;
    if (this.filtroRapidoVisivel) {
      setTimeout(() => {
        this.inputFiltroRapido?.nativeElement?.focus();
      }, 50);
    }
  }

  public limparFiltroRapido(): void {
    this.filtroRapido = '';
    this.filtroRapidoVisivel = false;
    this.dataSource.filter = '';
  }

  public obterValorCampo(row: any, campo: string): any {
    return row?.[campo];
  }

  public obterValorExibicao(row: any, coluna: ListaDadosColuna): any {
    const valor = this.obterValorCampo(row, coluna.campo);
    if (coluna.formatador) {
      return coluna.formatador(row, valor);
    }

    if (this.getTipoEditor(coluna) === 'combobox') {
      const opcao = coluna.edicao?.opcoes?.find((item: any) => this.saoIguais(this.obterValorOpcaoCombo(item, coluna), valor));
      if (opcao) {
        return this.obterRotuloOpcaoCombo(opcao, coluna);
      }
    }

    if (coluna.campoExibicao) {
      if (coluna.campoExibicao.includes('.')) {
        return coluna.campoExibicao.split('.').reduce((acc, curr) => acc?.[curr], row);
      }
      return this.obterValorCampo(row, coluna.campoExibicao);
    }

    if (this.getTipoEditor(coluna) === 'checkbox') {
      return valor ? 'Sim' : 'Não';
    }

    if (this.getTipoEditor(coluna) === 'data' && valor) {
      const data = valor instanceof Date ? valor : new Date(valor);
      if (!Number.isNaN(data.getTime())) {
        return data.toLocaleDateString('pt-BR');
      }
    }

    return valor;
  }

  public obterClasseValor(row: any, coluna: ListaDadosColuna): string | string[] | Record<string, boolean> {
    return coluna.classeValor ? coluna.classeValor(row, this.obterValorCampo(row, coluna.campo)) : '';
  }

  public obterCorValor(row: any, coluna: ListaDadosColuna): string | undefined {
    return coluna.corValor?.(row, this.obterValorCampo(row, coluna.campo));
  }

  public obterCorTextoValor(row: any, coluna: ListaDadosColuna): string | undefined {
    return coluna.corTextoValor?.(row, this.obterValorCampo(row, coluna.campo));
  }

  public obterIcone(row: any, coluna: ListaDadosColuna): string | undefined {
    return coluna.icone?.(row, this.obterValorCampo(row, coluna.campo));
  }

  public obterIconeSecundario(row: any, coluna: ListaDadosColuna): string | undefined {
    return coluna.iconeSecundario?.(row, this.obterValorCampo(row, coluna.campo));
  }

  public obterClasseIcone(row: any, coluna: ListaDadosColuna): string | string[] | Record<string, boolean> {
    return coluna.classeIcone ? coluna.classeIcone(row, this.obterValorCampo(row, coluna.campo)) : '';
  }

  public obterTextoTooltip(row: any, coluna: ListaDadosColuna): string {
    if (coluna.tooltip) {
      return coluna.tooltip(row);
    }
    const valor = this.obterValorCampo(row, coluna.campo);
    if (valor === null || valor === undefined || valor === '') {
      return '';
    }
    return `${valor}`.trim();
  }

  public obterValorOpcaoCombo(opcao: any, coluna: ListaDadosColuna): any {
    return coluna.edicao?.campoChave ? opcao[coluna.edicao.campoChave] : opcao.valor;
  }

  public obterRotuloOpcaoCombo(opcao: any, coluna: ListaDadosColuna): string {
    return coluna.edicao?.campoRotulo ? opcao[coluna.edicao.campoRotulo] : opcao.rotulo;
  }

  public obterOpcoesCombo(row: any, coluna: ListaDadosColuna): any[] {
    const opcoes = coluna.edicao?.opcoes ?? [];
    if (!coluna.edicao?.filtroCombo) {
      return opcoes;
    }
    return opcoes.filter((opcao: any) => coluna.edicao!.filtroCombo!(opcao, row));
  }

  public getTipoEditor(coluna: ListaDadosColuna): ListaDadosTipoEditor {
    return coluna.edicao?.tipoEditor ?? 'texto';
  }

  public podeEditarColuna(coluna: ListaDadosColuna): boolean {
    return this.upHabilitaEditarLinha && coluna.editavel !== false;
  }

  public isLinhaEmEdicao(row: any): boolean {
    return this.linhaEmEdicao === row;
  }

  public onAcaoEditar(row: any): void {
    if (this.upHabilitaEditarLinha && this.upPermitirEdicaoLinhaExistente) {
      this.onIniciarEdicaoLinha(row);
      return;
    }

    this.ueBotaoEditar.emit(row);
  }

  public isStatusAtivo(row: any, coluna: ListaDadosColuna): boolean {
    return !!this.obterValorCampo(row, coluna.campo);
  }

  public onIniciarEdicaoLinha(row: any): void {
    if (!this.upHabilitaEditarLinha || this.editandoLinhaAtiva) {
      return;
    }

    this.linhaEmEdicao = row;
    this.linhaCriadaEmEdicao = false;
    this.linhaOriginal = this.capturarValoresLinha(row);
    this.ueIniciarEdicaoLinha.emit(row);
    this.focarPrimeiraCelulaEditavel(row);
  }

  public onAcaoIncluir(): void {
    if (!this.upHabilitaEditarLinha) {
      this.ueBotaoIncluir.emit();
      return;
    }

    if (this.editandoLinhaAtiva) {
      return;
    }

    if (!Array.isArray(this.upDados)) {
      this.upDados = [];
    }

    const novaLinha = this.criarLinhaVazia();
    this.marcarLinhaComoNova(novaLinha);
    this.upDados.unshift(novaLinha);
    this.dataSource.data = this.upDados;

    this.linhaEmEdicao = novaLinha;
    this.linhaCriadaEmEdicao = true;
    this.linhaOriginal = this.capturarValoresLinha(novaLinha);
    this.aplicarFiltroRapido(this.filtroRapido);
    this.ueIniciarEdicaoLinha.emit(novaLinha);
    this.focarPrimeiraCelulaEditavel(novaLinha);
  }

  public onMudouValor(row: any, coluna: ListaDadosColuna, valor: any): void {
    this.ueMudouValorLinha.emit({ linha: row, campo: coluna.campo, valor });
  }

  public onSalvarLinha(row: any): void {
    if (!this.upHabilitaEditarLinha || !this.isLinhaEmEdicao(row) || !this.podeSalvarLinha(row)) {
      return;
    }

    this.ueSalvarLinha.emit(row);
  }

  public onCancelarLinha(row: any): void {
    if (!this.upHabilitaEditarLinha || !this.isLinhaEmEdicao(row)) {
      return;
    }

    if (this.temAlteracaoLinha(row)) {
      const dialogData: DialogData = {
        title: 'Confirmação',
        message: 'Aceita descartar as alterações?',
        buttonText: 'Cancelar',
        secondButtonText: 'Descartar',
      };

      const dialogRef = this.dialog.open(DialogComponent, { data: dialogData });
      dialogRef.afterClosed().subscribe(result => {
        if (result) {
          this.descartarEdicao(row);
          this.ueCancelarEdicaoLinha.emit(row);
          this.limparEdicao();
          this.aplicarFiltroRapido(this.filtroRapido);
        }
      });
      return;
    }

    this.descartarEdicao(row);
    this.ueCancelarEdicaoLinha.emit(row);
    this.limparEdicao();
    this.aplicarFiltroRapido(this.filtroRapido);
  }

  public podeSalvarLinha(row: any): boolean {
    if (!this.upHabilitaEditarLinha || this.upCarregando || !this.isLinhaEmEdicao(row)) {
      return false;
    }

    return this.colunasEditaveis.every(coluna => this.isCampoValido(row, coluna));
  }

  public isCampoValido(row: any, coluna: ListaDadosColuna): boolean {
    const validacao = coluna.edicao?.validacao;
    const valor = this.obterValorCampo(row, coluna.campo);
    const tipo = this.getTipoEditor(coluna);

    if (!validacao) {
      return true;
    }

    if (validacao.obrigatorio) {
      if (tipo === 'checkbox') {
        if (valor !== true) {
          return false;
        }
      } else if (valor === null || valor === undefined || `${valor}`.trim() === '') {
        return false;
      }
    }

    if (tipo === 'numero' && valor !== null && valor !== undefined && `${valor}` !== '') {
      const numero = Number(valor);
      if (Number.isNaN(numero)) {
        return false;
      }

      if (validacao.min !== undefined && numero < validacao.min) {
        return false;
      }

      if (validacao.max !== undefined && numero > validacao.max) {
        return false;
      }
    }

    if (tipo === 'data' && valor) {
      const data = valor instanceof Date ? valor : new Date(valor);
      if (Number.isNaN(data.getTime())) {
        return false;
      }
    }

    const texto = valor === null || valor === undefined ? '' : `${valor}`;

    if (validacao.minLength !== undefined && texto.length < validacao.minLength) {
      return false;
    }

    if (validacao.maxLength !== undefined && texto.length > validacao.maxLength) {
      return false;
    }

    if (validacao.regex && texto) {
      try {
        if (!new RegExp(validacao.regex).test(texto)) {
          return false;
        }
      } catch {
        return false;
      }
    }

    return true;
  }

  public abrirMenuLinha(row: any): void {
    this.linhaSelecionada = row;
  }

  public onCliqueEditar(): void {
    if (this.linhaSelecionada) {
      this.onAcaoEditar(this.linhaSelecionada);
    }
  }

  public onCliqueExcluir(): void {
    if (this.linhaSelecionada) {
      this.ueBotaoExcluir.emit(this.linhaSelecionada);
    }
  }

  public onCliqueAcaoLinha(acao: ListaDadosAcaoLinha): void {
    if (this.linhaSelecionada) {
      this.ueAcaoLinha.emit({ chave: acao.chave, linha: this.linhaSelecionada });
    }
  }

  public isAcaoLinhaVisivel(acao: ListaDadosAcaoLinha, row: any): boolean {
    if (!row) {
      return false;
    }

    return acao.visivel ? acao.visivel(row) : true;
  }

  public isAcaoLinhaDesabilitada(acao: ListaDadosAcaoLinha, row: any): boolean {
    if (!row) {
      return true;
    }

    return acao.desabilitada ? acao.desabilitada(row) : false;
  }

  public isBotaoEditarVisivel(row: any): boolean {
    return !!row && this.upVisivelBotaoEditar(row);
  }

  public isBotaoExcluirVisivel(row: any): boolean {
    return !!row && this.upVisivelBotaoExcluir(row);
  }

  public exibeMenuLinha(row: any): boolean {
    return !!row && !this.editandoLinhaAtiva &&
      (this.upHabilitaBotaoEditar || this.upHabilitaBotaoExcluir || this.upAcoesLinha.length > 0);
  }

  public temAcaoLinhaDisponivel(row: any): boolean {
    return !!row && (
      (this.upHabilitaBotaoEditar && this.isBotaoEditarVisivel(row)) ||
      (this.upHabilitaBotaoExcluir && this.isBotaoExcluirVisivel(row)) ||
      this.upAcoesLinha.some(acao => this.isAcaoLinhaVisivel(acao, row))
    );
  }

  public onMudouPagina(evento: PageEvent): void {
    if (this.upHabilitaPaginacao) {
      this.ueMudouPagina.emit(evento);
    }
  }

  private configurarPaginador(): void {
    this.dataSource.paginator = this.upHabilitaPaginacao ? null : this.paginator ?? null;
  }

  private capturarValoresLinha(row: any): Record<string, any> {
    const campos = this.upColunas.map(c => c.campo);
    return campos.reduce((acc, campo) => {
      acc[campo] = row?.[campo];
      return acc;
    }, {} as Record<string, any>);
  }

  private restaurarLinhaOriginal(row: any): void {
    if (!this.linhaOriginal) {
      return;
    }

    this.upColunas.forEach(coluna => {
      row[coluna.campo] = this.linhaOriginal?.[coluna.campo];
    });
  }

  private temAlteracaoLinha(row: any): boolean {
    if (!this.linhaOriginal) {
      return false;
    }

    return this.upColunas.some(coluna => !this.saoIguais(row?.[coluna.campo], this.linhaOriginal?.[coluna.campo]));
  }

  private saoIguais(valorA: any, valorB: any): boolean {
    if (valorA instanceof Date && valorB instanceof Date) {
      return valorA.getTime() === valorB.getTime();
    }

    return valorA === valorB;
  }

  private limparEdicao(): void {
    this.linhaEmEdicao = null;
    this.linhaOriginal = null;
    this.linhaCriadaEmEdicao = false;
  }

  private descartarEdicao(row: any): void {
    if (this.linhaCriadaEmEdicao) {
      this.removerLinha(row);
      return;
    }

    this.restaurarLinhaOriginal(row);
  }

  private removerLinha(row: any): void {
    if (!Array.isArray(this.upDados)) {
      return;
    }

    const index = this.upDados.indexOf(row);
    if (index >= 0) {
      this.upDados.splice(index, 1);
      this.dataSource.data = this.upDados;
      this.aplicarFiltroRapido(this.filtroRapido);
    }
  }

  private isLinhaNovaSemId(row: any): boolean {
    if (!row || !row[this.marcadorLinhaNova]) {
      return false;
    }

    const camposIdentificador = this.obterCamposIdentificador();
    if (camposIdentificador.length === 0) {
      return true;
    }

    return camposIdentificador.every(campo => this.isValorSemId(row?.[campo]));
  }

  private marcarLinhaComoNova(row: any): void {
    if (!row) {
      return;
    }

    Object.defineProperty(row, this.marcadorLinhaNova, {
      value: true,
      enumerable: false,
      configurable: true,
      writable: true,
    });

    const camposIdentificador = this.obterCamposIdentificador();
    camposIdentificador.forEach(campo => {
      if (row[campo] === undefined) {
        row[campo] = null;
      }
    });
  }

  private obterCamposIdentificador(): string[] {
    const linhaComId = (this.upDados ?? []).find(item => !!item && typeof item === 'object' &&
      Object.keys(item).some(chave => this.isCampoIdentificador(chave)));

    if (!linhaComId) {
      return [];
    }

    return Object.keys(linhaComId).filter(chave => this.isCampoIdentificador(chave));
  }

  private isCampoIdentificador(chave: string): boolean {
    if (!chave) {
      return false;
    }

    if (chave === 'id') {
      return true;
    }

    if (chave.startsWith('id_')) {
      return true;
    }

    return /^id[A-Z0-9].*/.test(chave);
  }

  private isValorSemId(valor: any): boolean {
    return valor === null || valor === undefined || valor === '' || valor === 0 || valor === '0';
  }

  private criarLinhaVazia(): any {
    return this.upColunas.reduce((acc, coluna) => {
      const tipo = this.getTipoEditor(coluna);

      switch (tipo) {
        case 'checkbox':
          acc[coluna.campo] = false;
          break;
        case 'numero':
          acc[coluna.campo] = null;
          break;
        case 'data':
          acc[coluna.campo] = null;
          break;
        case 'combobox':
          acc[coluna.campo] = null;
          break;
        case 'cor':
          acc[coluna.campo] = '#1976D2';
          break;
        default:
          acc[coluna.campo] = '';
          break;
      }

      return acc;
    }, {} as Record<string, any>);
  }

  private focarPrimeiraCelulaEditavel(row: any): void {
    setTimeout(() => {
      if (!this.isLinhaEmEdicao(row)) {
        return;
      }

      const container = document.querySelector('.lista-dados-card .mat-row.lista-dados-card__row--em-edicao') as HTMLElement | null;
      if (!container) {
        return;
      }

      const foco = container.querySelector(
        'input[matInput], textarea[matInput], mat-select, .mat-checkbox-input, button:not([disabled])'
      ) as HTMLElement | null;

      foco?.focus();
      if (foco instanceof HTMLInputElement) {
        foco.select();
      }
    });
  }

  private focarBotaoAdicionar(): void {
    setTimeout(() => {
      const botao = document.getElementById('lista-dados-botao-adicionar') as HTMLButtonElement | null;
      botao?.focus();
    });
  }
}
